// ============================================
// USUÁRIOS
// ============================================
export interface User {
  id: string;
  name: string;
  cpf?: string;
  rg?: string;
  email: string;
  phone?: string;
  role: string;
  companyId?: string;
  managerId?: string;
  bankName?: string;
  bankAgency?: string;
  bankAccount?: string;
  bankAccountType?: string;
  bankPixKey?: string;
  employmentType?: EmploymentType | null;
  pjCompanyName?: string;
  pjCnpj?: string;
  pjTradeName?: string;
  supplierId?: string;
  createdAt: number | string;
  updatedAt: number | string;
}

/** Regime de contratação do colaborador. */
export type EmploymentType = 'clt' | 'pj';

export type DocumentType =
  | 'cartao_cnpj'
  | 'comprovante_residencia'
  | 'contrato_social'
  | 'documentos_socios'
  | 'documento_identidade'
  | 'outro';

export interface UserDocument {
  id: string;
  userId: string;
  documentType: DocumentType;
  fileUrl: string | null;
  fileName: string;
  fileSize?: number;
  uploadedAt: string;
}

export const usersApi = {
    async update(id: string, data: { name: string; email: string; phone: string }): Promise<void> {
      const response = await authenticatedFetch(`${API_BASE_URL}/users/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.error || 'Erro ao atualizar usuário.');
      }
      return;
    },
  async listar(params?: { companyId?: string; role?: string; limit?: number; offset?: number }): Promise<{ data: User[]; pagination?: Pagination }> {
    const query = buildQueryString({
      company_id: params?.companyId,
      role: params?.role,
      limit: params?.limit,
      offset: params?.offset,
    });
    const response = await authenticatedFetch(`${API_BASE_URL}/users${query ? `?${query}` : ""}`);
    const result = (await response.json()) as ApiResponse<User[] & { pagination?: Pagination }>;
    if (result.error) {
      throw new Error(result.error);
    }
    return { data: (result as any).data || [], pagination: (result as any).pagination };
  },
  async criar(payload: Omit<User, "id" | "createdAt" | "updatedAt">): Promise<User> {
    // Corrige campos vazios para null
    const { ...rest } = payload as any;
    const fixedPayload = {
      ...rest,
      companyId: payload.companyId === '' ? null : payload.companyId,
      managerId: payload.managerId === '' ? null : payload.managerId,
    };
    delete fixedPayload.password;
    // Log temporário do payload enviado
    console.log('[FRONTEND] Payload enviado para criação de usuário:', fixedPayload);
    const response = await authenticatedFetch(`${API_BASE_URL}/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fixedPayload),
    });
    const result = (await response.json()) as ApiResponse<User> | User;
    if ('id' in result) {
      return result;
    }
    if (result.error) {
      throw new Error(result.error);
    }
    return result.data!;
  },
};

// ============================================
// EMPRESAS
// ============================================
export interface Company {
  id: string;
  corporateName: string;
  tradeName: string;
  cnpj: string;
  responsibleName: string;
  responsibleEmail: string;
  responsiblePhone: string;
  logoUrl?: string;
  isActive: boolean;
  createdAt: number | string;
  updatedAt: number | string;
}

export interface CompanyBankAccount {
  id: string;
  companyId: string;
  bankName: string;
  bankAgency: string;
  bankAccount: string;
  accountType: string;
  pixKey?: string;
  createdAt: number | string;
  updatedAt: number | string;
}

export const companiesApi = {
  async listar(params?: { cnpj?: string; isActive?: boolean; limit?: number; offset?: number }): Promise<{ data: Company[]; pagination?: Pagination }> {
    const query = buildQueryString({
      cnpj: params?.cnpj,
      isActive: params?.isActive === undefined ? undefined : String(params.isActive),
      limit: params?.limit,
      offset: params?.offset,
    });
    const response = await authenticatedFetch(`${API_BASE_URL}/companies${query ? `?${query}` : ""}`);
    const result = (await response.json()) as ApiResponse<Company[] & { pagination?: Pagination }>;
    if (result.error) {
      throw new Error(result.error);
    }
    return { data: (result as any).data || [], pagination: (result as any).pagination };
  },
  async criar(payload: Omit<Company, "id" | "createdAt" | "updatedAt">): Promise<Company> {
    const response = await authenticatedFetch(`${API_BASE_URL}/companies`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const result = (await response.json()) as ApiResponse<Company>;
    if (result.error) {
      throw new Error(result.error);
    }
    return result.data!;
  },
};
// Use only the origin in dev, so endpoints remain /api/...

function normalizeApiBaseUrl(rawBaseUrl?: string): string {
  const fallbackBaseUrl = '/api';
  if (!rawBaseUrl) return fallbackBaseUrl;

  const baseUrl = rawBaseUrl.trim().replace(/\/$/, '');

  if (baseUrl === '') return fallbackBaseUrl;
  if (baseUrl === '/api' || baseUrl.endsWith('/api')) return baseUrl;

  return `${baseUrl}/api`;
}

// Sempre use a variável de ambiente VITE_API_BASE_URL
const API_BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL);

if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.log(`[api] [DEV] API_BASE_URL(raw): ${import.meta.env.VITE_API_BASE_URL || '(empty)'}`);
  // eslint-disable-next-line no-console
  console.log(`[api] [DEV] API_BASE_URL(normalized): ${API_BASE_URL}`);
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

// Wrapper para adicionar autenticação JWT
async function authenticatedFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const token = localStorage.getItem('token');
  const headers = {
    ...(init?.headers || {}),
    Authorization: token ? `Bearer ${token}` : '',
  };
  const finalInit = { ...init, headers };
  // Log detalhado para depuração
  console.log('[FRONTEND] authenticatedFetch URL:', input);
  console.log('[FRONTEND] authenticatedFetch headers:', headers);
  if (finalInit.body) {
    try {
      console.log('[FRONTEND] authenticatedFetch payload:', JSON.parse(finalInit.body as string));
    } catch {
      console.log('[FRONTEND] authenticatedFetch payload (raw):', finalInit.body);
    }
  }
  const response = await fetch(input, finalInit);
  // Log da resposta
  const clone = response.clone();
  let responseBody;
  try {
    responseBody = await clone.json();
  } catch {
    responseBody = await clone.text();
  }
  console.log('[FRONTEND] authenticatedFetch status:', response.status);
  console.log('[FRONTEND] authenticatedFetch response:', responseBody);
  return response;
}


function handleApiError(error: any, url: string, response?: Response) {
  let message = `Erro ao acessar a API: ${url}\n`;
  if (response) {
    message += `Status: ${response.status} ${response.statusText}\n`;
  }
  message += error?.message ? `${error.message}\n` : "";
  message += "Sugestão: Verifique se o backend está rodando e acessível.";
  return message;
}

// ============================================
// NOTAS FISCAIS
// ============================================

type Pagination = { limit: number; offset: number; count: number };

const buildQueryString = (params: Record<string, string | number | undefined>) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");

const requireFields = (payload: Record<string, unknown>, required: string[]) => {
  const missing = required.filter((key) => !payload[key]);
  if (missing.length) {
    throw new Error(`Campos obrigatórios faltando: ${missing.join(", ")}`);
  }
};

export type FeedbackStatus = "rascunho" | "enviado" | "concluido";
export type FeedbackLifecycleStatus = "draft" | "published" | "released";

export interface Feedback {
  id: string;
  collaboratorId: string;
  managerId: string;
  collaborator?: { id: string; name: string; email: string };
  manager?: { id: string; name: string; email: string };
  referencePeriod: string;
  behaviorRating: number;
  behaviorComment: string;
  deliveriesRating: number;
  deliveriesComment: string;
  qualityRating: number;
  qualityComment: string;
  collaborationRating: number;
  collaborationComment: string;
  punctualityRating: number;
  punctualityComment: string;
  strengths: string;
  improvements: string;
  recommendations: string;
  status: FeedbackStatus;
  lifecycleStatus: FeedbackLifecycleStatus;
  releasedAt?: string | null;
  releasedBy?: string | null;
  evaluationPeriodId?: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface CreateFeedback {
  collaboratorId: string;
  managerId: string;
  referencePeriod: string;
  behaviorRating: number;
  behaviorComment: string;
  deliveriesRating: number;
  deliveriesComment: string;
  qualityRating: number;
  qualityComment: string;
  collaborationRating: number;
  collaborationComment: string;
  punctualityRating: number;
  punctualityComment: string;
  strengths: string;
  improvements: string;
  recommendations: string;
  status?: FeedbackStatus;
}

export interface UpdateFeedback {
  status: FeedbackStatus;
}

export const feedbacksApi = {
  async listar(params?: {
    collaboratorId?: string;
    managerId?: string;
    status?: FeedbackStatus;
    limit?: number;
    offset?: number;
  }): Promise<{ data: Feedback[]; pagination?: Pagination }> {
    const query = buildQueryString({
      collaborator_id: params?.collaboratorId,
      manager_id: params?.managerId,
      status: params?.status,
      limit: params?.limit,
      offset: params?.offset,
    });

    const response = await authenticatedFetch(`/api/feedbacks${query ? `?${query}` : ""}`);
    const result = (await response.json()) as ApiResponse<Feedback[] & { pagination?: Pagination }>;
    
    if (result.error) {
      throw new Error(result.error);
    }
    
    return { data: (result as any).data || [], pagination: (result as any).pagination };
  },

  async criar(data: CreateFeedback): Promise<Feedback> {
    requireFields(data as unknown as Record<string, unknown>, [
      "collaboratorId",
      "managerId",
      "referencePeriod",
      "behaviorRating",
      "deliveriesRating",
      "qualityRating",
      "collaborationRating",
      "punctualityRating",
    ]);
    const response = await authenticatedFetch('/api/feedbacks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    
    const result = await response.json() as ApiResponse<Feedback>;
    
    if (result.error) {
      throw new Error(result.error);
    }
    
    return result.data!;
  },

  async atualizar(id: string, data: UpdateFeedback): Promise<Feedback> {
    requireFields({ id, status: data.status }, ["id", "status"]);
    const response = await authenticatedFetch(`/api/feedbacks/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    
    const result = await response.json() as ApiResponse<Feedback>;
    
    if (result.error) {
      throw new Error(result.error);
    }
    
    return result.data!;
  }
};

// ============================================
// AUTENTICAÇÃO
// ============================================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  data: {
    id: string;
    email: string;
    name: string;
    role: 'admin' | 'finance' | 'governance' | 'manager' | 'collaborator' | 'supplier';
    companyId?: string;
    managerId?: string;
    supplierId?: string;
    clientId?: string | null;
    employmentType?: EmploymentType | null;
    isFirstLogin: boolean;
    mustChangePassword: boolean;
    token: string;
    refreshToken?: string;
  };
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// Armazenamento do token JWT
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
}

export function getAuthToken(): string | null {
  if (!authToken) {
    authToken = localStorage.getItem('token');
  }
  return authToken;
}

/**
 * Erros do backend passam pelo VersionHateoasFilter, que devolve `message`
 * (string, ou array quando o class-validator recusa o payload).
 */
interface AuthMessageResponse {
  message?: string | string[];
  error?: string;
}

function extractErrorMessage(body: AuthMessageResponse, fallback: string): string {
  if (Array.isArray(body.message)) return body.message.join('. ');
  return body.message || body.error || fallback;
}

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse['data']> {
    const response = await fetch('/api/public/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const result = await response.json() as ApiResponse<LoginResponse['data']>;
    
    if (result.error) {
      throw new Error(result.error);
    }
    
    if (!result.data) {
      throw new Error('Resposta inválida do servidor');
    }
    
    if (result.data.token) {
      setAuthToken(result.data.token);
    }

    return result.data;
  },

  async refreshAccessToken(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
    const response = await fetch('/api/public/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    const result = await response.json() as ApiResponse<{ token: string; refreshToken: string }>;
    if (!response.ok || result.error || !result.data) {
      throw new Error(result.error ?? 'Falha ao renovar sessão');
    }
    return result.data;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Não autenticado');
    }
    
    const response = await fetch('/api/users/change-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    });
    
    const result = await response.json() as ApiResponse<{ message: string }>;
    
    if (result.error) {
      throw new Error(result.error);
    }
  },

  /**
   * Dispara o e-mail de redefinição. O backend responde a mesma mensagem
   * exista ou não a conta — não tratamos "e-mail não encontrado" na UI.
   */
  async forgotPassword(email: string): Promise<string> {
    const response = await fetch('/api/public/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const result = await response.json() as AuthMessageResponse;

    if (!response.ok) {
      throw new Error(extractErrorMessage(result, 'Não foi possível enviar o e-mail de redefinição'));
    }

    return typeof result.message === 'string'
      ? result.message
      : 'Se houver uma conta com este e-mail, enviaremos um link para redefinir a senha.';
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await fetch('/api/public/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });

    const result = await response.json() as AuthMessageResponse;

    if (!response.ok) {
      throw new Error(extractErrorMessage(result, 'Não foi possível redefinir a senha'));
    }
  },

  async getCurrentUser(): Promise<LoginResponse['data'] | null> {
    const token = getAuthToken();
    if (!token) {
      return null;
    }
    
    const response = await fetch('/api/public/auth/me', {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    const result = await response.json() as ApiResponse<LoginResponse['data']>;
    
    if (result.error) {
      return null;
    }
    
    return result.data || null;
  },

  async logout(): Promise<void> {
    setAuthToken(null);
    await fetch('/api/public/auth/logout', { method: 'POST' });
  },

  async initAuthTable(): Promise<void> {
    const response = await fetch('/api/public/auth/init', { method: 'POST' });
    
    const result = await response.json() as ApiResponse<{ message: string }>;
    
    if (result.error) {
      throw new Error(result.error);
    }
  },

  async createAdmin(email: string, password: string, name: string): Promise<void> {
    const response = await fetch('/api/public/auth/create-admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name })
    });
    
    const result = await response.json() as ApiResponse<{ message: string }>;
    
    if (result.error) {
      throw new Error(result.error);
    }
  }
};
